import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import MobileLayout from "../components/MobileLayout";
import AuthForm from "../components/auth/AuthForm";
import { useAuth } from "../contexts/AuthContext";
import { ChevronRightIcon } from "@heroicons/react/24/outline";

const GUEST_EMAIL = "a28d3d4c-3fb2-492a-abb7-8c9c2edcd547-guest@rupertspia.com";

const Login: React.FC = () => {
  const { login, continueAsGuest, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isGuestLoading, setIsGuestLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isGuestUser = user?.email === GUEST_EMAIL;
  const capacitorPlatform = Capacitor.getPlatform();
  const isIosPlatform =
    capacitorPlatform === "ios" ||
    (typeof navigator !== "undefined" &&
      /iPhone|iPad|iPod/i.test(navigator.userAgent));

  // Check for expired session
  useEffect(() => {
    const expired = new URLSearchParams(location.search).get("expired");
    if (expired) {
      setError("Your session has expired. Please log in again.");
    }
  }, [location.search]);

  // Redirect if already logged in (except guest: let guest see login page so they can sign in with another account)
  useEffect(() => {
    if (user && !isGuestUser) {
      const redirectPath = sessionStorage.getItem("redirectAfterLogin");
      if (redirectPath) {
        sessionStorage.removeItem("redirectAfterLogin");
        navigate(redirectPath);
      } else {
        navigate("/");
      }
    }
  }, [user, isGuestUser, navigate]);

  const redirectAfterLogin = () => {
    const redirectPath = sessionStorage.getItem("redirectAfterLogin");
    if (redirectPath) {
      sessionStorage.removeItem("redirectAfterLogin");
      navigate(redirectPath);
    } else {
      navigate("/");
    }
  };

  const handleLogin = async (data: { email: string; password: string }) => {
    setIsLoading(true);
    setError(null);

    try {
      await login(data.email, data.password);
      redirectAfterLogin();
    } catch (error) {
      const errorMessage = (error as Error).message;
      console.error("Login error details:", error);
      console.error("Error message:", errorMessage);

      // Show user-friendly error messages
      if (
        errorMessage.includes("Unable to connect to server") ||
        errorMessage.includes("Failed to fetch")
      ) {
        setError(
          "Unable to connect to server. Please check your internet connection."
        );
      } else if (errorMessage === "Session expired") {
        // setError("Your session has expired. Please log in again.");
        setError(
          "Email or password is incorrect. If you need further help, email luminous@rupertspira.com"
        );
      } else if (
        errorMessage.includes("Not Found") ||
        errorMessage.includes("404")
      ) {
        setError("API endpoint not found. Please check server configuration.");
      } else if (
        errorMessage.includes("401") ||
        errorMessage.includes("Unauthorized")
      ) {
        setError(
          "Email or password is incorrect. If you need further help, email luminous@rupertspira.com"
        );
      } else if (errorMessage.includes("HTTP")) {
        setError(`Server error: ${errorMessage}`);
      } else if (
        errorMessage === "Unable to process your request. Please try again."
      ) {
        setError(errorMessage);
      } else {
        // Show the actual error message from the server
        setError(
          errorMessage ||
            "Login failed. Please check your credentials and try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueAsGuest = async () => {
    setIsGuestLoading(true);
    setError(null);
    try {
      await continueAsGuest();
      redirectAfterLogin();
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(
        errorMessage ||
          "Guest login failed. Please check your connection and try again."
      );
    } finally {
      setIsGuestLoading(false);
    }
  };

  return (
    <MobileLayout title="Sign In">
      <div className="py-4">
        <div className="text-center">
          <h2 className="text-3xl font-garamond text-brand-primary">
            Welcome back
          </h2>
          <p className="text-gray-600 dark:text-grey-300 py-2">
            Please sign in to your account
          </p>
        </div>

        <AuthForm
          mode="login"
          onSubmit={handleLogin}
          error={error || undefined}
          isLoading={isLoading}
        />

        <div className="text-center font-garamond space-y-1">
          {isIosPlatform ? (
            <>
              <p className="text-gray-600 dark:text-grey-300">
                Don't have an account?
              </p>
              <p className="text-gray-600 dark:text-grey-300">
                <Link
                  to="/register"
                  className="text-brand-primary hover:underline"
                >
                  Create one
                </Link>{" "}
                <span>or</span>{" "}
                <button
                  type="button"
                  onClick={handleContinueAsGuest}
                  disabled={isGuestLoading}
                  className="text-brand-primary hover:underline bg-transparent border-0 p-0 cursor-pointer font-inherit disabled:opacity-50"
                >
                  Continue as Guest
                </button>
              </p>
            </>
          ) : (
            <p className="text-gray-600 dark:text-grey-300">
              Don't have an account?{" "}
              <Link
                to="/register"
                className="text-brand-primary hover:underline"
              >
                Create one
              </Link>
            </p>
          )}
        </div>

        <div className="text-center mt-4 mb-2">
          <div className="mx-auto max-w-xs px-4 py-2 bg-amber-50 border border-amber-100 rounded-md">
            <p className="text-amber-700 text-xs">
            This login is separate from rupertspira.com

            </p>
          </div>
        </div>

        {/* About Link */}
        <div className="mt-8 pt-4 border-t border-brand-primary/10 flex flex-row gap-10 justify-center">
          <div className="flex items-center justify-center">
            <Link
              to="/about"
              className="group flex items-center space-x-2 text-gray-500 dark:text-grey-300 hover:text-brand-primary dark:hover:text-brand-primary transition-colors duration-300 font-garamond"
            >
              <span>About Luminous</span>
              <ChevronRightIcon className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
          <div className="flex items-center justify-center font-garamond">
            <Link
              to="/privacy-policy"
              className="text-brand-primary hover:underline"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </MobileLayout>
  );
};

export default Login;
